import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  getSite,
  getDirectory,
  updateDirectory,
  createDirectory,
  getTenants,
  getClient,
  getTenantDirSelections,
  getDirectoryDeviceSelections,
  getDevices,
  deleteUserDir,
  insertUserDir,
  deleteDeviceDir,
  insertDeviceDir,
  validateDirectoryAssignment,
} from "./directoryService";

import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Spinner } from "react-bootstrap";

import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  FormGroup,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  Col,
  Media,
} from "reactstrap";

import SimpleHeader from "components/Headers/SimpleHeader.js";

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        Show{" "}
        <select
          name="datatable-basic_length"
          aria-controls="datatable-basic"
          className="form-control form-control-sm"
          onChange={(e) => onSizePerPageChange(e.target.value)}
          value={currSizePerPage}
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>{" "}
        entries.
      </label>
    </div>
  ),
});

function DirectoriesForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const { directory_uuid } = useParams();
  const dirId = directory_uuid;

  const [dirName, setDirName] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteId, setSiteId] = useState(null);
  const [clientId, setClientId] = useState(null);

  const [users, setUsers] = useState([]);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [availableTenantSearch, setAvailableTenantSearch] = useState("");
  const [selectedTenantSearch, setSelectedTenantSearch] = useState("");

  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const loading = initialLoading || saving;

  const availableTenants = useMemo(() => {
    const q = availableTenantSearch.toLowerCase();

    return users
      .filter((user) => !selectedTenants.includes(user.tenant_uuid))
      .filter((user) => {
        return (
          (user.user_name || "").toLowerCase().includes(q) ||
          (user.v_number || "").toLowerCase().includes(q) ||
          (user.phone1 || "").toLowerCase().includes(q) ||
          (user.phone2 || "").toLowerCase().includes(q) ||
          (user.phone3 || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));
  }, [users, selectedTenants, availableTenantSearch]);

  const selectedTenantRows = useMemo(() => {
    const q = selectedTenantSearch.toLowerCase();

    return users
      .filter((user) => selectedTenants.includes(user.tenant_uuid))
      .filter((user) => {
        return (
          (user.user_name || "").toLowerCase().includes(q) ||
          (user.v_number || "").toLowerCase().includes(q) ||
          (user.phone1 || "").toLowerCase().includes(q) ||
          (user.phone2 || "").toLowerCase().includes(q) ||
          (user.phone3 || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));
  }, [users, selectedTenants, selectedTenantSearch]);

  useEffect(() => {
    const { site_uuid } = getSite();
    const { client_uuid } = getClient();
    setSiteId(site_uuid || null);
    setClientId(client_uuid || null);
  }, []);

  useEffect(() => {
    const loadDirectoryName = async () => {
      if (!dirId || !siteId) return;

      try {
        setInitialLoading(true);
        const dirData = await getDirectory(dirId);
        setDirName(dirData.dir_name);
      } catch (error) {
        alert("Error loading directory: " + error.message);
        navigate("/admin/hrms/directories");
      } finally {
        setInitialLoading(false);
      }
    };

    loadDirectoryName();
  }, [dirId, siteId, navigate]);

  useEffect(() => {
    const loadDirectoryData = async () => {
      if (!siteId) return;

      try {
        setInitialLoading(true);

        const allTenants = await getTenants(siteId);
        setUsers(allTenants || []);

        const allDevices = await getDevices(siteId);
        setDevices(allDevices || []);
        setFilteredDevices(allDevices || []);

        if (dirId) {
          const userDir = await getTenantDirSelections(dirId, siteId);
          const selectedUserIds = userDir.map((item) => item.tenant_uuid);
          setSelectedTenants(selectedUserIds);

          const deviceDir = await getDirectoryDeviceSelections(dirId, siteId);
          const selectedDeviceIds = deviceDir.map((d) => d.device_uuid);
          setSelectedDevices(selectedDeviceIds);
        }
      } catch (error) {
        console.error("Error loading directory data:", error);
        setUsers([]);
        setDevices([]);
        setFilteredDevices([]);
      } finally {
        setInitialLoading(false);
      }
    };

    loadDirectoryData();
  }, [siteId, dirId]);

  const headerStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
    color: isDark ? "#e5e7eb" : "#111827",
  };

  const searchInputStyles = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    color: isDark ? "#e5e7eb" : "#111827",
    border: "none",
  };

  const searchAddonStyles = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    color: isDark ? "#9ca3af" : "#6b7280",
    border: "none",
  };

  const searchWrapperStyles = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    border: isDark ? "1px solid #374151" : "1px solid #ced4da",
  };

  const addTenant = (tenantUuid) => {
    setSelectedTenants((prev) =>
      prev.includes(tenantUuid) ? prev : [...prev, tenantUuid]
    );
  };

  const handleSelectAllAvailableTenants = () => {
    setSelectedTenants((prev) => [
      ...prev,
      ...availableTenants
        .map((tenant) => tenant.tenant_uuid)
        .filter((tenantUuid) => !prev.includes(tenantUuid)),
    ]);
  };

  const handleRemoveAllSelectedTenants = () => {
    setSelectedTenants([]);
  };

  const removeTenant = (tenantUuid) => {
    setSelectedTenants((prev) => prev.filter((id) => id !== tenantUuid));
  };

  const handleSelectAllDevices = () => {
    if (
      selectedDevices.length === filteredDevices.length &&
      filteredDevices.length > 0
    ) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map((device) => device.device_uuid));
    }
  };

  const tenantColumns = [
    {
      dataField: "user_name",
      text: "Tenant",
      sort: true,
      formatter: (cell) => (
        <Media className="align-items-center">
          <a className="avatar rounded-circle mr-3">
            <img alt="Tenant" src={require("assets/img/theme/tenant-icon.png")} />
          </a>
          <Media>
            <span className="name mb-0 text-sm">{cell}</span>
          </Media>
        </Media>
      ),
    },
    { dataField: "v_number", text: "Virtual Number", sort: true },
    { dataField: "phone1", text: "Phone 1", sort: true },
    { dataField: "phone2", text: "Phone 2", sort: true },
    { dataField: "phone3", text: "Phone 3", sort: true },
  ];

  const deviceColumns = [
    {
      dataField: "checkbox",
      text: "",
      headerFormatter: () => (
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input"
            id="checkbox-select-all-devices"
            checked={
              selectedDevices.length === filteredDevices.length &&
              filteredDevices.length > 0
            }
            onChange={handleSelectAllDevices}
          />
          <label
            className="custom-control-label"
            htmlFor="checkbox-select-all-devices"
          />
        </div>
      ),
      headerStyle: { width: "40px" },
      style: { textAlign: "center" },
    },
    {
      dataField: "device_name",
      text: "Name",
      sort: true,
      formatter: (cell, row) => (
        <Media className="align-items-center">
          <a className="avatar rounded-circle mr-3">
            <img
              alt="Device"
              src={
                row.device_model === "2N IP Style"
                  ? require("assets/img/devices/2nipstyle.png")
                  : row.device_model === "2N IP Verso"
                    ? require("assets/img/devices/2nipverso.jpg")
                    : require("assets/img/devices/2nipstyle.png")
              }
            />
          </a>
          <Media>
            <span className="name mb-0 text-sm">{cell}</span>
          </Media>
        </Media>
      ),
    },
    { dataField: "device_model", text: "Brand/Model", sort: true },
    {
      dataField: "online_status",
      text: "Online Status",
      formatter: (cell, row) => (
        <Badge color="" className="badge-dot mr-4">
          <i
            className={
              row.last_checkin &&
                Date.now() - new Date(row.last_checkin).getTime() <=
                5 * 60 * 1000
                ? "bg-success"
                : "bg-danger"
            }
          />
          <span className="status">
            {row.last_checkin &&
              Date.now() - new Date(row.last_checkin).getTime() <= 5 * 60 * 1000
              ? "Online"
              : "Offline"}
          </span>
        </Badge>
      ),
    },
    {
      dataField: "last_checkin",
      text: "Last Checkin",
      formatter: (cell) =>
        cell ? new Date(cell).toLocaleString() : "Never Connected",
    },
    {
      dataField: "device_ip",
      text: "WAN IP Address",
      formatter: (cell) => cell || "N/A",
    },
  ];

  const saveUserDeviceSelectionsWithDirId = async (dirId) => {
    if (!siteId || !dirId) return false;

    try {
      await validateDirectoryAssignment({
        site_uuid: siteId,
        dir_uuid: dirId,
        tenant_uuids: selectedTenants,
        device_uuids: selectedDevices,
      });

      await deleteUserDir(siteId, dirId);
      await deleteDeviceDir(siteId, dirId);

      if (selectedTenants.length > 0) {
        const userRows = selectedTenants.map((tenant_uuid) => ({
          site_uuid: siteId,
          dir_uuid: dirId,
          tenant_uuid,
        }));

        await insertUserDir(userRows);
      }

      if (selectedDevices.length > 0) {
        const deviceRows = selectedDevices.map((device_uuid) => ({
          site_uuid: siteId,
          dir_uuid: dirId,
          device_uuid,
        }));

        await insertDeviceDir(deviceRows);
      }

      return true;
    } catch (err) {
      console.error("Error saving user/device selections:", err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dirName.trim()) {
      alert("Directory name cannot be empty.");
      return;
    }

    if (!siteId) {
      alert("No site selected.");
      return;
    }

    setSaving(true);

    try {
      if (dirId) {
        await updateDirectory(dirId, {
          directory: {
            site_uuid: siteId,
            dir_name: dirName,
            client_uuid: clientId,
          },
        });

        const saveSuccess = await saveUserDeviceSelectionsWithDirId(dirId);
        if (!saveSuccess) {
          alert("Error saving selections. Please try again.");
          setSaving(false);
          return;
        }
      } else {
        const newDir = await createDirectory({
          directory: {
            site_uuid: siteId,
            dir_name: dirName,
            client_uuid: clientId,
          },
        });

        const saveSuccess = await saveUserDeviceSelectionsWithDirId(
          newDir.dir_uuid
        );

        if (!saveSuccess) {
          alert("Error saving selections. Please try again.");
          setSaving(false);
          return;
        }
      }

      navigate("/admin/hrms/directories");
    } catch (err) {
      console.error("Error saving directory:", err);
      alert("Error saving directory: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SimpleHeader
        name={dirId ? "Edit Directory" : "Add Directory"}
        parentName="HRMS"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader style={headerStyle}>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                      {dirId ? "Edit Directory" : "Add Directory"}
                    </h3>
                  </Col>
                  <Col xs="4" className="d-flex justify-content-end">
                    <Button
                      color="danger"
                      onClick={() => navigate("/admin/hrms/directories")}
                      size="sm"
                    >
                      Back to Directories
                    </Button>
                    <Button color="success" onClick={handleSubmit} size="sm">
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form>
                  <FormGroup>
                    <Label for="dir_name">
                      Directory Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="dir_name"
                      name="dir_name"
                      placeholder="Enter directory name"
                      type="text"
                      value={dirName}
                      onChange={(e) => setDirName(e.target.value)}
                      disabled={loading}
                    />
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            <Card className="mt-4">
              <CardHeader style={headerStyle}>
                <Row className="align-items-center">
                  <Col md="6" className="d-flex align-items-center">
                    <h3 className="mb-0">Available Tenants</h3>
                    <Badge
                      color="info"
                      pill
                      className="ml-3"
                      style={{ fontSize: "0.8rem", padding: "0.45rem 0.75rem" }}
                    >
                      {availableTenants.length} available
                    </Badge>
                  </Col>

                  <Col md="6" className="d-flex align-items-center">
                    <h3 className="mb-0">Selected Tenants</h3>
                    <Badge
                      color="success"
                      pill
                      className="ml-3"
                      style={{ fontSize: "0.8rem", padding: "0.45rem 0.75rem" }}
                    >
                      {selectedTenants.length} selected
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col xl="6" className="mb-4 mb-lg-0">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <small
                          className="d-block mb-2"
                          style={{ color: isDark ? "#d1d5db" : "#6b7280" }}
                        >
                          Click a tenant to add them.
                        </small>

                        <Button
                          color="primary"
                          size="sm"
                          onClick={handleSelectAllAvailableTenants}
                          disabled={loading || availableTenants.length === 0}
                        >
                          Select All
                        </Button>
                      </div>

                      <FormGroup className="mb-0" style={{ width: "260px" }}>
                        <InputGroup
                          className="shadow-sm"
                          style={{
                            ...searchWrapperStyles,
                            borderRadius: "999px",
                            overflow: "hidden",
                          }}
                        >
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText
                              style={{ ...searchAddonStyles, borderRadius: 0 }}
                            >
                              <i className="fas fa-search" />
                            </InputGroupText>
                          </InputGroupAddon>

                          <Input
                            placeholder="Search available..."
                            type="text"
                            value={availableTenantSearch}
                            style={{ ...searchInputStyles, borderRadius: 0 }}
                            onChange={(e) => setAvailableTenantSearch(e.target.value)}
                          />
                        </InputGroup>
                      </FormGroup>
                    </div>

                    <div className={isDark ? "table-dark-mode" : ""}>
                      <ToolkitProvider
                        keyField="tenant_uuid"
                        data={availableTenants}
                        columns={tenantColumns}
                      >
                        {(props) => (
                          <div className="py-2 table-responsive">
                            <BootstrapTable
                              {...props.baseProps}
                              bootstrap4
                              pagination={pagination}
                              bordered={false}
                              classes="align-items-center table-flush"
                              rowStyle={() => ({
                                cursor: "pointer",
                              })}
                              rowEvents={{
                                onClick: (e, row) => addTenant(row.tenant_uuid),
                              }}
                              noDataIndication={() =>
                                loading ? (
                                  <div className="text-center">Loading...</div>
                                ) : (
                                  <div className="text-center">
                                    No available tenants.
                                  </div>
                                )
                              }
                            />
                          </div>
                        )}
                      </ToolkitProvider>
                    </div>
                  </Col>

                  <Col xl="6">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <small
                          className="d-block mb-2"
                          style={{ color: isDark ? "#d1d5db" : "#6b7280" }}
                        >
                          Click a tenant to remove them.
                        </small>

                        <Button
                          color="danger"
                          size="sm"
                          onClick={handleRemoveAllSelectedTenants}
                          disabled={loading || selectedTenants.length === 0}
                        >
                          Remove All
                        </Button>
                      </div>

                      <FormGroup className="mb-0" style={{ width: "260px" }}>
                        <InputGroup
                          className="shadow-sm"
                          style={{
                            ...searchWrapperStyles,
                            borderRadius: "999px",
                            overflow: "hidden",
                          }}
                        >
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText
                              style={{ ...searchAddonStyles, borderRadius: 0 }}
                            >
                              <i className="fas fa-search" />
                            </InputGroupText>
                          </InputGroupAddon>

                          <Input
                            placeholder="Search selected..."
                            type="text"
                            value={selectedTenantSearch}
                            style={{ ...searchInputStyles, borderRadius: 0 }}
                            onChange={(e) => setSelectedTenantSearch(e.target.value)}
                          />
                        </InputGroup>
                      </FormGroup>
                    </div>

                    <div className={isDark ? "table-dark-mode" : ""}>
                      <ToolkitProvider
                        keyField="tenant_uuid"
                        data={selectedTenantRows}
                        columns={tenantColumns}
                      >
                        {(props) => (
                          <div className="py-2 table-responsive">
                            <BootstrapTable
                              {...props.baseProps}
                              bootstrap4
                              pagination={pagination}
                              bordered={false}
                              classes="align-items-center table-flush"
                              rowStyle={() => ({
                                cursor: "pointer",
                                backgroundColor: "#088717",
                                color: "#ffffff",
                              })}
                              rowClasses={() => "selected-row"}
                              rowEvents={{
                                onClick: (e, row) =>
                                  removeTenant(row.tenant_uuid),
                              }}
                              noDataIndication={() =>
                                loading ? (
                                  <div className="text-center">Loading...</div>
                                ) : (
                                  <div className="text-center">
                                    No selected tenants.
                                  </div>
                                )
                              }
                            />
                          </div>
                        )}
                      </ToolkitProvider>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mt-4">
              <CardHeader style={headerStyle}>
                <div className="d-flex align-items-center">
                  <h3 className="mb-0">Select Enterphones</h3>
                  <FormGroup className="mb-0 ml-3" style={{ width: "250px" }}>
                    <InputGroup
                      className="shadow-sm"
                      style={{
                        ...searchWrapperStyles,
                        borderRadius: "999px",
                        overflow: "hidden",
                      }}
                    >
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText
                          style={{ ...searchAddonStyles, borderRadius: 0 }}
                        >
                          <i className="fas fa-search" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Search enterphones..."
                        type="text"
                        style={{ ...searchInputStyles, borderRadius: 0 }}
                        onChange={(e) => {
                          if (e.target.value === "") {
                            setFilteredDevices(devices);
                          } else {
                            const filtered = devices.filter(
                              (device) =>
                                device.device_name
                                  .toLowerCase()
                                  .includes(e.target.value.toLowerCase()) ||
                                (device.device_model &&
                                  device.device_model
                                    .toLowerCase()
                                    .includes(e.target.value.toLowerCase()))
                            );
                            setFilteredDevices(filtered);
                          }
                        }}
                      />
                    </InputGroup>
                  </FormGroup>
                </div>
              </CardHeader>

              <CardBody>
                <div className={isDark ? "table-dark-mode" : ""}>
                  <ToolkitProvider
                    keyField="device_uuid"
                    data={filteredDevices}
                    columns={deviceColumns}
                  >
                    {(props) => (
                      <div className="py-4 table-responsive">
                        <BootstrapTable
                          {...props.baseProps}
                          bootstrap4
                          pagination={pagination}
                          bordered={false}
                          classes="align-items-center table-flush"
                          rowStyle={(row) => ({
                            cursor: "pointer",
                            backgroundColor: selectedDevices.includes(
                              row.device_uuid
                            )
                              ? "#088717"
                              : "transparent",
                            color: selectedDevices.includes(row.device_uuid)
                              ? "#ffffff"
                              : undefined,
                          })}
                          rowClasses={(row) =>
                            selectedDevices.includes(row.device_uuid)
                              ? "selected-row"
                              : ""
                          }
                          rowEvents={{
                            onClick: (e, row) => {
                              setSelectedDevices((prev) =>
                                prev.includes(row.device_uuid)
                                  ? prev.filter((id) => id !== row.device_uuid)
                                  : [...prev, row.device_uuid]
                              );
                            },
                          }}
                          noDataIndication={() =>
                            loading ? (
                              <div className="text-center">Loading...</div>
                            ) : (
                              <div className="text-center">
                                No devices found for the selected site.
                              </div>
                            )
                          }
                        />
                      </div>
                    )}
                  </ToolkitProvider>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {(initialLoading || saving) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isDark
              ? "rgba(0,0,0,0.55)"
              : "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: "1.5rem 2rem",
              borderRadius: "1rem",
              backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
              color: isDark ? "#f8f9fa" : "#111827",
              border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
              textAlign: "center",
              minWidth: 260,
            }}
          >
            <Spinner animation="border" variant={isDark ? "light" : "primary"} />
            <div className="mt-3" style={{ fontWeight: 600 }}>
              {saving ? "Saving directory…" : "Loading directory…"}
            </div>
            <div style={{ opacity: 0.8, fontSize: "0.9rem" }}>
              Please do not close this page.
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

export default DirectoriesForm;
